/**
 * SaveContactPage (/connect) — "Save SLIK Connect to your contacts" (Jose 2026-10-22).
 *
 * Goal: clients save the shop's text line as "SLIK Connect" so every confirmation / reminder / reply
 * shows a NAME they recognize — never an unknown number that reads as spam. Linked from texts, emails,
 * the booking confirmation, and the review request. One tap → iOS/Android "Add Contact" sheet via the
 * static /slik-connect.vcf (served as text/vcard — see firebase.json header). #118.
 */
import React from 'react';
import { ShieldCheck, MessageSquareText, Phone } from 'lucide-react';

const SLIK_NUMBER = '(855) 754-5602';

const SaveContactPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#20242e] to-[#2A211A] flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="bg-[#20242e] px-6 pt-8 pb-6 text-center">
          {/* WHITE beard mark on the dark hero (Jose 2026-11-01): the default dark mark was invisible here. */}
          <img src="/assets/branding/beard-mark-white.png" alt="SLIK" className="w-16 h-16 object-contain mx-auto mb-3" />
          <h1 className="text-2xl font-extrabold text-white">Save SLIK Connect</h1>
          <p className="text-white/70 text-sm mt-1">Our official text line · {SLIK_NUMBER}</p>
        </div>

        <div className="px-6 py-6">
          <p className="text-gray-700 text-[15px] leading-relaxed mb-5">
            Save us to your contacts so you <b>always know it’s us</b>. Your booking confirmations,
            reminders, and replies will show up as <b>“SLIK Connect”</b> — not an unknown number that
            looks like spam.
          </p>

          <a
            href="/slik-connect.vcf"
            className="block w-full text-center py-4 rounded-2xl bg-gradient-to-r from-slik-purple to-slik-magenta text-white font-bold text-lg shadow-lg hover:opacity-95 transition-opacity"
          >
            Add to Contacts
          </a>
          <p className="text-center text-xs text-gray-400 mt-2">Tap → “Add Contact” → Done.</p>

          <div className="mt-6 space-y-3">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-slik-purple flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">Know it’s really us — never a scam or wrong number.</p>
            </div>
            <div className="flex items-start gap-3">
              <MessageSquareText className="w-5 h-5 text-slik-purple flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600">Text us back anytime to book, reschedule, or ask a question.</p>
            </div>
          </div>

          <a
            href={`sms:+18557545602`}
            className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-2xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
          >
            <Phone className="w-4 h-4" /> Text SLIK
          </a>
        </div>
      </div>
    </div>
  );
};

export default SaveContactPage;
